import { z } from 'zod';

// ============================================================================
// Admin configuration endpoints
// ============================================================================

export const UpdateTicketTypePriceRequestSchema = z.object({
  basePriceIdr: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
});

export type UpdateTicketTypePriceRequest = z.infer<typeof UpdateTicketTypePriceRequestSchema>;

export const UpdateVatRequestSchema = z.object({
  vatPercentage: z.number().int(),
});

export type UpdateVatRequest = z.infer<typeof UpdateVatRequestSchema>;

export const UpdatePricingConfigRequestSchema = z.record(z.unknown());

export type UpdatePricingConfigRequest = z.infer<typeof UpdatePricingConfigRequestSchema>;

export const TogglePriceLockRequestSchema = z.object({
  ticketTypeId: z.string().min(1, 'ticketTypeId is required'),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be in YYYY-MM-DD format'),
  lock: z.boolean(),
});

export type TogglePriceLockRequest = z.infer<typeof TogglePriceLockRequestSchema>;

export const GetTicketTypeHistoryQuerySchema = z.object({
  limit: z.coerce.number().int().positive().max(200).optional(),
});

export type GetTicketTypeHistoryQuery = z.infer<typeof GetTicketTypeHistoryQuerySchema>;

// ============================================================================
// Response schemas
// ============================================================================

// GET /admin/pricing/ticket-types — each item from getBasePrices()
export const TicketTypeBasePriceSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  basePrice: z.number().int().nonnegative(),
  isActive: z.boolean(),
});

export type TicketTypeBasePrice = z.infer<typeof TicketTypeBasePriceSchema>;

// GET/PUT /admin/pricing/vat
export const VatConfigResponseSchema = z.object({
  vatPercentage: z.number().int(),
  label: z.string(),
  isIncluded: z.boolean(),
});

export type VatConfigResponse = z.infer<typeof VatConfigResponseSchema>;

// GET/PUT /admin/pricing/config
export const PricingConfigResponseSchema = z.object({
  dynamicPricingEnabled: z.boolean(),
  minMultiplier: z.number(),
  maxMultiplier: z.number(),
  defaultMultiplier: z.number(),
  lockHoursBeforeVisit: z.number().int(),
  vatPercentage: z.number().int(),
});

export type PricingConfigResponse = z.infer<typeof PricingConfigResponseSchema>;

// POST /admin/pricing/lock
export const PriceLockToggleResponseSchema = z.object({
  ticketTypeId: z.string(),
  date: z.string(),
  isLocked: z.boolean(),
});

export type PriceLockToggleResponse = z.infer<typeof PriceLockToggleResponseSchema>;

// GET /admin/pricing/ticket-types/:id/history — one row from priceHistory table
export const PriceHistoryEntrySchema = z.object({
  id: z.string(),
  ticketTypeId: z.string(),
  date: z.string(),
  basePrice: z.number().int().nonnegative(),
  multiplier: z.number(),
  finalPrice: z.number().int().nonnegative(),
  multiplierSource: z.string(),
  isLocked: z.boolean(),
  lockedAt: z.string().nullable(),
  lockedBy: z.string().nullable(),
  createdAt: z.string(),
});

export type PriceHistoryEntry = z.infer<typeof PriceHistoryEntrySchema>;

// GET /admin/pricing/scheduled — one row from priceMultiplier table (future-dated only)
export const ScheduledPriceChangeSchema = z.object({
  id: z.string(),
  ticketTypeId: z.string(),
  dateFrom: z.string(),
  dateTo: z.string(),
  multiplier: z.number(),
  reason: z.string(),
  priority: z.number().int(),
  isActive: z.boolean(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type ScheduledPriceChange = z.infer<typeof ScheduledPriceChangeSchema>;
